<!-- Hero Section -->
<section class="relative py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
  <div class="absolute inset-0 opacity-20">
    <img src="https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?w=1920&h=800&fit=crop" alt="" class="w-full h-full object-cover">
  </div>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <nav class="text-sm text-white/70 mb-6">
      <a routerLink="/" class="hover:text-white">Home</a>
      <span class="mx-2">/</span>
      <a routerLink="/messaging" class="hover:text-white">Messaging Services</a>
      <span class="mx-2">/</span>
      <span class="text-white">WhatsApp API</span>
    </nav>
    <div class="text-center text-white">
      <div class="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6">
        WHATSAPP BUSINESS API
      </div>
      <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">WhatsApp API</h1>
      <p class="text-xl text-white/90 max-w-3xl mx-auto mb-8">
        Build powerful conversational experiences with enterprise WhatsApp Business API. Automate support, send notifications, and create intelligent chatbots.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <a routerLink="/contact" class="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-all shadow-lg">
          Get API Access
        </a>
        <a href="#features" class="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all">
          View Documentation
        </a>
      </div>
    </div>
  </div>
</section>

<!-- Features Section -->
<section id="features" class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Enterprise-Grade API Features</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Everything you need to build sophisticated WhatsApp experiences
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      @for (feature of features; track feature.title) {
        <div class="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
          <div class="w-14 h-14 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center mb-6">
            <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="feature.icon"></path>
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">{{ feature.title }}</h3>
          <p class="text-gray-600 leading-relaxed">{{ feature.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- Process Section -->
<section class="py-20 bg-gradient-to-br from-primary-50 to-primary-100">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Integration Process</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        From setup to production in four straightforward steps
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      @for (step of process; track step.step) {
        <div class="relative">
          <div class="bg-white p-8 rounded-xl shadow-lg h-full">
            <div class="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center font-bold text-xl text-white mb-6">
              {{ step.step }}
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">{{ step.title }}</h3>
            <p class="text-gray-600 leading-relaxed">{{ step.description }}</p>
          </div>
        </div>
      }
    </div>
  </div>
</section>

<!-- Case Study Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Success Story</h2>
      <p class="text-xl text-gray-600">How WhatsApp API transforms customer support at scale</p>
    </div>

    <div class="bg-gradient-to-br from-primary-50 to-primary-100 rounded-3xl overflow-hidden">
      <div class="grid lg:grid-cols-2 gap-0">
        <div class="p-8 lg:p-12">
          <div class="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
            {{ caseStudy.industry }}
          </div>
          <h3 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{{ caseStudy.title }}</h3>
          <div class="mb-6">
            <span class="font-semibold text-gray-900">Client:</span>
            <span class="text-gray-700 ml-2">{{ caseStudy.client }}</span>
          </div>

          <div class="mb-6">
            <h4 class="font-bold text-gray-900 mb-2">Challenge:</h4>
            <p class="text-gray-600 leading-relaxed">{{ caseStudy.challenge }}</p>
          </div>

          <div class="mb-6">
            <h4 class="font-bold text-gray-900 mb-2">Solution:</h4>
            <p class="text-gray-600 leading-relaxed">{{ caseStudy.solution }}</p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            @for (result of caseStudy.results; track result.metric) {
              <div class="bg-white rounded-xl p-4 shadow-sm text-center">
                <div class="text-xl font-bold text-primary-600">{{ result.value }}</div>
                <div class="text-sm text-gray-600">{{ result.metric }}</div>
              </div>
            }
          </div>
        </div>
        <div class="h-64 lg:h-auto">
          <img [src]="caseStudy.image" [alt]="caseStudy.title" class="w-full h-full object-cover">
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Testimonial Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center">
      <div class="flex justify-center mb-6">
        @for (star of [1,2,3,4,5]; track star) {
          <svg class="w-8 h-8 text-yellow-300 fill-current" viewBox="0 0 20 20">
            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
          </svg>
        }
      </div>
      <blockquote class="text-xl md:text-2xl text-white mb-8 leading-relaxed italic">
        "{{ testimonial.content }}"
      </blockquote>
      <div class="flex items-center justify-center">
        <img [src]="testimonial.avatar" [alt]="testimonial.name" class="w-16 h-16 rounded-full object-cover mr-4 border-4 border-white">
        <div class="text-left text-white">
          <div class="font-bold text-lg">{{ testimonial.name }}</div>
          <div class="text-white/80">{{ testimonial.role }}</div>
          <div class="text-white/70 font-semibold">{{ testimonial.company }}</div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-white">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ready to Build with WhatsApp API?</h2>
    <p class="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
      Start creating powerful conversational experiences. Get API access and comprehensive developer resources today.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center">
      <a routerLink="/contact" class="btn-primary">Get API Access</a>
      <a routerLink="/messaging" class="btn-secondary">View All Services</a>
    </div>
  </div>
</section>
